import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  FiCreditCard as FeatureTicketIcon,
  FiShield,
  FiSmartphone,
  FiBarChart2,
  FiHeadphones,
  FiEdit3,
} from "react-icons/fi";
import { selectUser } from "../../store/slice/user";
import { selectCountries } from "../../store/slice/countries";
import SubPageNav from "../../components/SubPageNav";
import SiteFooter from "../../components/SiteFooter";
import { getStaticPage } from "../../services/data/staticPages";
import { Style } from "./style";

const featureIcons = [<FeatureTicketIcon />, <FiShield />, <FiSmartphone />, <FiBarChart2 />, <FiHeadphones />, <FiEdit3 />];

// Mirrors new-design/o7events-frontend/how-it-works.html: hero, "For
// Attendees" 4-step flow, "For Organizers" 4-step flow, Key Features grid,
// and a "Ready to Get Started?" CTA band. Content is backend-driven (admin
// "Static Pages" > How It Works) with the original hardcoded copy kept as a
// fallback so the page never breaks/flashes empty while the backend content
// is being filled in.
const HowItWorks = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { language, countryId } = useSelector(selectUser);
  const countries = useSelector(selectCountries);
  const isArabic = language === "ar";

  const activeCountry = countries.find((c) => c?.id === countryId);
  const region =
    activeCountry?.translation?.[language]?.name || t("marketing.nav.everywhere");

  const [apiData, setApiData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getStaticPage("how-it-works")
      .then((res) => {
        if (!cancelled) setApiData(res.data.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const tr = (item, field) =>
    item?.translation?.[language]?.[field] || item?.translation?.en?.[field] || null;

  const heroItem = apiData?.hero?.[0];
  const heroTitle = tr(heroItem, "title") || t("marketing.howItWorks.title");
  const heroSubtitle = tr(heroItem, "subtitle") || t("marketing.howItWorks.subtitle");

  const hardcodedAttendeeSteps = [
    { title: t("marketing.footer.browseEvents"), body: t("marketing.howItWorks.exploreEventsBody", { region }) },
    { title: t("marketing.howItWorks.bookYourTickets"), body: t("marketing.howItWorks.bookYourTicketsBody") },
    { title: t("marketing.howItWorks.getTicketsInstantly"), body: t("marketing.howItWorks.getTicketsInstantlyBody") },
    { title: t("marketing.howItWorks.enjoyEvent"), body: t("marketing.howItWorks.enjoyEventBody") },
  ];
  const attendeeSteps =
    apiData?.attendee_step?.length
      ? apiData.attendee_step.map((s) => ({ title: tr(s, "title"), body: tr(s, "body") }))
      : hardcodedAttendeeSteps;

  const hardcodedOrganizerSteps = [
    { title: t("marketing.home.createYourEvent"), body: t("marketing.howItWorks.hiwEasySetupBody") },
    { title: t("marketing.howItWorks.promoteAndSell"), body: t("marketing.howItWorks.promoteAndSellBody") },
    { title: t("marketing.howItWorks.manageAttendees"), body: t("marketing.howItWorks.manageAttendeesBody") },
    { title: t("marketing.howItWorks.getPaid"), body: t("marketing.howItWorks.getPaidBody") },
  ];
  const organizerSteps =
    apiData?.organizer_step?.length
      ? apiData.organizer_step.map((s) => ({ title: tr(s, "title"), body: tr(s, "body") }))
      : hardcodedOrganizerSteps;

  const hardcodedFeatures = [
    { icon: <FeatureTicketIcon />, title: t("marketing.howItWorks.digitalTickets"), body: t("marketing.howItWorks.digitalTicketsBody") },
    { icon: <FiShield />, title: t("marketing.howItWorks.securePayments"), body: t("marketing.howItWorks.securePaymentsBody") },
    { icon: <FiSmartphone />, title: t("marketing.footer.mobileApp"), body: t("marketing.howItWorks.manageEverythingBody") },
    { icon: <FiBarChart2 />, title: t("marketing.home.realTimeAnalytics"), body: t("marketing.howItWorks.trackSalesBody") },
    { icon: <FiHeadphones />, title: t("marketing.howItWorks.support247"), body: t("marketing.howItWorks.support247Body") },
    { icon: <FiEdit3 />, title: t("marketing.howItWorks.hiwCustomBranding"), body: t("marketing.howItWorks.hiwCustomBrandingBody") },
  ];
  const features =
    apiData?.feature?.length
      ? apiData.feature.map((f, i) => ({
          icon: featureIcons[i] || featureIcons[featureIcons.length - 1],
          title: tr(f, "title"),
          body: tr(f, "body"),
        }))
      : hardcodedFeatures;

  const ctaItem = apiData?.cta?.[0];
  const ctaTitle = tr(ctaItem, "title") || t("marketing.howItWorks.readyToGetStarted");
  const ctaSubtitle = tr(ctaItem, "subtitle") || t("marketing.howItWorks.readyToGetStartedSubtitle");
  const ctaButtonLabel = tr(ctaItem, "button_label") || t("marketing.footer.browseEvents");
  const ctaButtonLabel2 = tr(ctaItem, "button_label_2") || t("marketing.nav.createEvent");

  const renderSteps = (steps) => (
    <Box sx={Style.steps}>
      {steps.map((s, i) => (
        <Box key={i} sx={Style.step(isArabic)}>
          <Box sx={Style.stepNumber}>{i + 1}</Box>
          <Box>
            <Typography component="h3" sx={Style.stepTitle(isArabic)}>
              {s.title}
            </Typography>
            <Typography sx={Style.stepBody(isArabic)}>{s.body}</Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );

  return (
    <Box sx={Style.page}>
      <SubPageNav />
      <Box sx={Style.hero}>
        <Typography component="h1" sx={Style.h1(isArabic)}>
          {heroTitle}
        </Typography>
        <Typography sx={Style.heroSubtitle(isArabic)}>
          {heroSubtitle}
        </Typography>
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.h2(isArabic)}>
          {t("marketing.footer.forAttendees")}
        </Typography>
        {renderSteps(attendeeSteps)}
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.h2(isArabic)}>
          {t("marketing.footer.forOrganizers")}
        </Typography>
        {renderSteps(organizerSteps)}
      </Box>

      <Box sx={Style.section}>
        <Typography component="h2" sx={Style.h2(isArabic)}>
          {t("marketing.howItWorks.keyFeatures")}
        </Typography>
        <Box sx={Style.featuresGrid}>
          {features.map((f, i) => (
            <Box key={i} sx={Style.feature}>
              <Box sx={Style.featureIcon}>{f.icon}</Box>
              <Typography component="h3" sx={Style.featureTitle(isArabic)}>
                {f.title}
              </Typography>
              <Typography sx={Style.featureBody(isArabic)}>{f.body}</Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <Box sx={Style.ctaSection}>
        <Typography component="h2" sx={Style.h2(isArabic)}>
          {ctaTitle}
        </Typography>
        <Typography sx={Style.ctaSubtitle(isArabic)}>
          {ctaSubtitle}
        </Typography>
        <Box sx={Style.ctaButtons(isArabic)}>
          <Box component="button" sx={Style.btnPrimary} onClick={() => navigate("/")}>
            {ctaButtonLabel}
          </Box>
          <Box
            component="button"
            sx={Style.btnOutline}
            onClick={() => navigate("/contact-sales")}
          >
            {ctaButtonLabel2}
          </Box>
        </Box>
      </Box>
      <SiteFooter />
    </Box>
  );
};

export default HowItWorks;
